import { AppShell, Button, Flex, Group, Notification, Text } from "@mantine/core";
import { MdMeetingRoom } from "react-icons/md";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import MultiPlacerLogo from "../../components/MultiPlacerLogo";
import classes from "./AuthenticatedPages.module.css";
import { FaDraftingCompass } from "react-icons/fa";
import { useAppSelector } from "../../app/hooks";
import PingToMl from "../../PingToMl";
import { ProjectPage } from "../../ProjectPage";
import { Projects } from "../../Projects";
import Extractedtrainingtata from "../../Extractectrainingdata";
import AddTrainingData from "../../../AddTrainingData";
import AddProject from "../../AddProject";
import LoginPage from "../../LoginPage/LoginPage";
import { useAccessToken } from "../../useAccessToken";
import { useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { loginRequest } from "../../authConfig";
import { UserMenu } from "../../UserMenu";
import { ProjectSwitcher } from "../../ProjectSwitcher";
import { GlobalBottomProgress } from "../../components/GlobalBottomProgress";
import { FaCircleCheck, FaTriangleExclamation } from "react-icons/fa6";
import HealthModal from "../../HealthModal";
import { useGetAuthMeQuery } from "../../../openapi";
import { FiSettings } from "react-icons/fi";
import { TrainingDataPage } from "../../trainingdata/TrainingData";

export function AuthenticatedPages() {
  const { instance } = useMsal();
  const { accessToken, isLoading, error } = useAccessToken();
  const [openHealthModal, setOpenHealthModal] = useState(false);
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
  const location = useLocation();
  const redirectedRef = useRef(false);

  //hämta projectNo från url
  const projectNo = location.pathname.split("/")[1];

  // Redirecta EN gång när vi vet att vi inte är auth
  useEffect(() => {
    if (isLoading || error || accessToken || redirectedRef.current) return;
    redirectedRef.current = true;
    // Skicka med var vi ska tillbaka efter login
    instance.loginRedirect({
      ...loginRequest,
      redirectStartPage: location.pathname + location.search,
    });
  }, [isLoading, error, accessToken, instance, location]);
  const toast = useAppSelector((state) => state.design.toast);

  useGetAuthMeQuery();
  if (error) return <div>Något gick fel: {error}</div>;
  if (isLoading) return <div>Laster...</div>;
  if (!accessToken) return <div>Redirectar till login…</div>;

  return (
    <>
      <GlobalBottomProgress />
      {openHealthModal === true && <HealthModal onClose={() => setOpenHealthModal(false)} />}
      <AppShell h="100dvh">
        {toast.isOpen && (
          <Notification
            withCloseButton={false}
            title={toast.header}
            color={toast.isSuccess ? "teal" : "red"}
            variant={toast.isSuccess ? "filled" : "filled"}
            radius="lg"
            style={{
              position: "fixed", // säkrare än absolute
              zIndex: 1000,
              bottom: 24,
              right: 24,
              width: 480, // större
              padding: 20,
              boxShadow: "0 16px 40px rgba(0,0,0,.28)",
              backgroundColor: toast.isSuccess ? "white" : "red",
            }}
            icon={
              toast.isSuccess ? (
                <FaCircleCheck />
              ) : (
                <FaTriangleExclamation /> // tydlig varningsikon
              )
            }
            // 👇 Överstyr textfärgerna så de blir vita på röd bakgrund
            styles={(theme) => ({
              root: {
                background: toast.isSuccess ? theme.white : theme.colors.red[6],
              },
              title: {
                color: toast.isSuccess ? theme.colors.gray[9] : theme.white,
                fontSize: 18,
                fontWeight: 800,
              },
              description: {
                color: toast.isSuccess ? theme.colors.gray[7] : theme.white,
                fontSize: 16,
                lineHeight: 1.5,
              },
              icon: {
                color: toast.isSuccess ? theme.colors.teal[6] : theme.white,
              },
            })}
          >
            {toast.text}
          </Notification>
        )}
        <AppShell.Header bg={"dark.7"}>
          <Group justify="center" px="md" w="100%">
            <Group
              className={classes.mainPadding}
              align="center"
              justify="space-between"
              wrap="nowrap"
            >
              <Group className={classes.group}>
                <Flex align="center" to="/" component={Link}>
                  <MultiPlacerLogo height={50} width={150} />
                </Flex>
                {projectNo !== "" && (
                  <>
                    <ProjectSwitcher width={200} defaultTail="design" />

                    <Button
                      leftSection={<FaDraftingCompass />}
                      data-current-page={location.pathname.endsWith("/design")}
                      to={`/${projectNo}/design`}
                      component={Link}
                      variant="header"
                      style={{ cursor: "pointer" }}
                    >
                      Design
                    </Button>

                    <Button
                      leftSection={<MdMeetingRoom />}
                      data-current-page={location.pathname.endsWith("/room")}
                      to={`/${projectNo}/room`}
                      component={Link}
                      variant="header"
                      style={{ cursor: "pointer" }}
                    >
                      Rom
                    </Button>

                    <Button
                      leftSection={<FiSettings />}
                      data-current-page={location.pathname.endsWith("/settings")}
                      to={`/${projectNo}/settings`}
                      component={Link}
                      variant="header"
                      style={{ cursor: "pointer" }}
                    >
                      Innstillinger
                    </Button>
                  </>
                )}
              </Group>
              <Group wrap="nowrap">
                {/* <Button
                  variant="outline"
                  size="sm"
                  color="gray"
                  onClick={() => setOpenHealthModal(true)}
                >
                  Driftssjekk
                </Button> */}
                {account && <UserMenu />}
              </Group>
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Main h="100%" className="overflow-hidden">
          <Group align="flex-start" className={`${classes.mainPadding}`} h="100%">
            <Routes>
              <Route path={"/addproject"} element={<AddProject />} />
              <Route path={"/"} element={<Projects />} />
              <Route path="/:projectId/*" element={<ProjectPage />} />
              <Route path={"/test"} element={<LoginPage />} />
              <Route path={"/trainingdata"} element={<TrainingDataPage />} />
              <Route path={"/extracted"} element={<Extractedtrainingtata />} />
              <Route path={"/addtrainingdata"} element={<AddTrainingData />} />
            </Routes>
          </Group>
        </AppShell.Main>
      </AppShell>
    </>
  );
}

function removeNonSerializableValues(obj: any): any {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeNonSerializableValues);
  }

  const newObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (typeof value !== "function" && typeof value !== "symbol") {
        newObj[key] = removeNonSerializableValues(value);
      }
    }
  }
  return newObj;
}
